import { useState, useEffect, useCallback } from 'react';
import { useAnnotation } from '../contexts/AnnotationContext';

export const useTimeSignatures = () => {
  // Get time signature data from context
  const { 
    generalInfo,
    timeSignatures: contextTimeSignatures,
    handleTimeSignatureAdd,
    handleTimeSignatureUpdate,
    handleTimeSignatureDelete,
    handleBatchTimeSignatureChanges: contextHandleBatchTimeSignatureChanges
  } = useAnnotation();

  const measures = generalInfo?.duration_number_of_bars ?? 0;
  // State for time signatures and beats per measure
  const [timeSignatures, setTimeSignatures] = useState([])
  const [beatsPerMeasureMap, setBeatsPerMeasureMap] = useState([]);
  const [timeSignatureChanges, setTimeSignatureChanges] = useState([]);
  
  // State for time signature selector
  const [showTimeSignatureSelector, setShowTimeSignatureSelector] = useState(false);
  const [activeMeasure, setActiveMeasure] = useState(null);
  const [selectorPosition, setSelectorPosition] = useState({ x: 0, y: 0 });

  // Resize arrays whenever measures changes
  useEffect(() => {                                                    // <<< ADDED >>>
    if (measures === 0) return;
    setTimeSignatures(prev =>
      prev.length === measures ? prev : Array(measures).fill('4/4')
    );
    setBeatsPerMeasureMap(prev =>
      prev.length === measures ? prev : Array(measures).fill(4)
    );
  }, [measures]);

  // Update local state when context time signatures change
  useEffect(() => {
    if (!contextTimeSignatures || contextTimeSignatures.length === 0 || measures === 0) return;
    
    setTimeSignatureChanges([...contextTimeSignatures]);
    
    // Update timeSignatures and beatsPerMeasureMap based on contextTimeSignatures
    updateTimeSignaturesAndBeatsPerMeasure(contextTimeSignatures);
  }, [contextTimeSignatures, measures]);
  
  // Handle time signature click
  const handleTimeSignatureClick = (measureIndex, event) => {
    if (measureIndex >= timeSignatures.length) return;
    setActiveMeasure(measureIndex);
    setSelectorPosition({ 
      x: event.clientX, 
      y: event.clientY 
    });
    setShowTimeSignatureSelector(true);
  };

  // Handle time signature change
  const handleTimeSignatureChange = (measureIndex, newTimeSignature) => {
    const newTimeSignatures = [...timeSignatures];
    newTimeSignatures[measureIndex-1] = newTimeSignature;
    setTimeSignatures(newTimeSignatures);
    
    // Update beats per measure for this measure
    updateBeatsPerMeasureForTimeSignature(measureIndex-1, newTimeSignature);
    
    // Add or update time signature change in context
    updateTimeSignatureChanges(measureIndex, newTimeSignature);
    setShowTimeSignatureSelector(false);
  };
  
  // Update beats per measure for a specific time signature
  const updateBeatsPerMeasureForTimeSignature = (index, timeSignature) => {
    const newBeatsPerMeasureMap = [...beatsPerMeasureMap];
    
    // Parse time signature (e.g., "4/4", "3/4", etc.)
    const [numerator] = timeSignature.split('/').map(num => parseInt(num, 10));
    if (!isNaN(numerator)) {
      newBeatsPerMeasureMap[index] = numerator;
    } else {
      // Default cases
      if (timeSignature === "4/4") {
        newBeatsPerMeasureMap[index] = 4;
      } else if (timeSignature === "3/4") {
        newBeatsPerMeasureMap[index] = 3;
      } else if (timeSignature === "2/4") {
        newBeatsPerMeasureMap[index] = 2;
      } else if (timeSignature === "6/8") {
        newBeatsPerMeasureMap[index] = 6;
      } else if (timeSignature === "9/8") {
        newBeatsPerMeasureMap[index] = 9;
      } else if (timeSignature === "12/8") {
        newBeatsPerMeasureMap[index] = 12;
      }
    }
    
    setBeatsPerMeasureMap(newBeatsPerMeasureMap);
  };

  // Handle batch time signature change
  const handleBatchTimeSignatureChanges = (ranges) => {
    if (!ranges || ranges.length === 0) return;
    
    // Call context method
    const result = contextHandleBatchTimeSignatureChanges(ranges);
    
    if (result && result.success) {
      // Update local state directly for immediate UI response
      const newTimeSignatures = [...timeSignatures];
      const newBeatsPerMeasureMap = [...beatsPerMeasureMap];
      
      for (const range of ranges) {
        for (let i = range.start - 1; i < range.end; i++) {
          if (i < newTimeSignatures.length) {
            newTimeSignatures[i] = range.timeSignature;
            
            // Update beats per measure
            const [numerator] = range.timeSignature.split('/').map(num => parseInt(num, 10));
            if (!isNaN(numerator)) {
              newBeatsPerMeasureMap[i] = numerator;
            }
          }
        }
      }
      
      setTimeSignatures(newTimeSignatures);
      setBeatsPerMeasureMap(newBeatsPerMeasureMap);
    }
    
    return result;
  };

  // Close time signature selector
  const handleCloseSelector = () => {
    setShowTimeSignatureSelector(false);
  };

  // Update time signature changes in the context
  const updateTimeSignatureChanges = (measureIndex, newTimeSignature) => {
    // Find if there's already a time signature at this measure
    const existingIndex = timeSignatureChanges.findIndex(
      ts => ts.position.bar === measureIndex
    );
    
    if (existingIndex >= 0) {
      // Update existing time signature
      const updatedTimeSignature = {
        position: {
          bar: measureIndex,
          beat: 1,
          subdivision: 1
        },
        value: newTimeSignature
      };
      
      handleTimeSignatureUpdate(existingIndex, updatedTimeSignature);
    } else {
      // Add new time signature
      const newTimeSignatureChange = {
        position: {
          bar: measureIndex,
          beat: 1,
          subdivision: 1
        },
        value: newTimeSignature
      };
      
      handleTimeSignatureAdd(newTimeSignatureChange);
    }
  };
  
  // Update timeSignatures and beatsPerMeasureMap based on time signature changes
  const updateTimeSignaturesAndBeatsPerMeasure = useCallback((changes) => {
    if (!changes || changes.length === 0) return;
    
    // Sort changes by bar
    const sortedChanges = [...changes].sort((a, b) => a.position.bar - b.position.bar);
    
    // Start with default time signatures
    const newTimeSignatures = Array(measures).fill("4/4");
    const newBeatsPerMeasureMap = Array(measures).fill(4);
    
    // Apply each change
    for (let i = 0; i < sortedChanges.length; i++) {
      const change = sortedChanges[i];
      const startBar = change.position.bar - 1;
      const value = change.value;
      
      // Determine the end bar (exclusive)
      const endBar = i < sortedChanges.length - 1 
        ? sortedChanges[i + 1].position.bar - 1 
        : measures;
      
      // Apply time signature for this range
      for (let bar = startBar; bar < endBar && bar < measures; bar++) {
        newTimeSignatures[bar] = value;
        
        // Update beats per measure
        const [numerator] = value.split('/').map(num => parseInt(num, 10));
        if (!isNaN(numerator)) {
          newBeatsPerMeasureMap[bar] = numerator;
        }
      }
    }
    
    setTimeSignatures(newTimeSignatures);
    setBeatsPerMeasureMap(newBeatsPerMeasureMap);
  }, [measures]);

  return {
    timeSignatures,
    beatsPerMeasureMap,
    showTimeSignatureSelector,
    activeMeasure,
    selectorPosition,
    handleTimeSignatureClick,
    handleTimeSignatureChange,
    handleBatchTimeSignatureChanges,
    handleCloseSelector,
    timeSignatureChanges
  };
}; 